import { apiClient } from '../lib/apiClient';

/** New-tank mode (Reef Guard #1825): tank stage, cycle tracker, livestock maturity notices. */
export type TankStage = 'unknown' | 'cycling' | 'young' | 'establishing' | 'mature';
export type CoralType = 'sps' | 'lps' | 'soft' | 'unknown';
export type LivestockCategory = 'coral' | 'fish' | 'invertebrate' | 'other';

export interface LatestCycleTest {
  readonly value: number;
  readonly unit: string;
  readonly measuredAt: string;
}

export interface MaturityStatus {
  readonly setupDate: string | null;
  readonly ageDays: number | null;
  readonly stage: TankStage;
  readonly cycle: {
    readonly complete: boolean;
    readonly status: 'not_started' | 'in_progress' | 'complete';
    readonly source: 'computed' | 'override' | 'assumed' | 'unknown';
    readonly completedAt: string | null;
    readonly cleanSets: number;
    readonly override: boolean | null;
  };
  readonly latest: Readonly<Record<'ammonia' | 'nitrite' | 'nitrate', LatestCycleTest | null>>;
  readonly askSetupDate: boolean;
}

export interface GateNotice {
  readonly gate: 'coral_before_cycle' | 'sps_unstable' | 'fish_pace';
  readonly severity: 'warning' | 'hint';
  readonly confidence: 'medium' | 'low';
  readonly variant: string;
  readonly values: Readonly<Record<string, string | number>>;
  readonly source: string;
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/maturity`;
export const maturityKey = (aquariumId: string) => ['maturity', aquariumId] as const;

export async function getMaturity(aquariumId: string): Promise<MaturityStatus> {
  return (await apiClient.get<{ data: MaturityStatus }>(base(aquariumId))).data;
}

export async function setCycleOverride(aquariumId: string, complete: boolean | null): Promise<MaturityStatus> {
  return (await apiClient.put<{ data: MaturityStatus }>(`${base(aquariumId)}/cycle-override`, { complete })).data;
}

export async function setSetupDate(aquariumId: string, setupDate: string): Promise<MaturityStatus> {
  return (await apiClient.put<{ data: MaturityStatus }>(`${base(aquariumId)}/setup-date`, { setup_date: setupDate })).data;
}

export async function dismissSetupPrompt(aquariumId: string): Promise<MaturityStatus> {
  return (await apiClient.post<{ data: MaturityStatus }>(`${base(aquariumId)}/setup-prompt/dismiss`, {})).data;
}

export async function checkLivestock(aquariumId: string, category: LivestockCategory, coralType?: CoralType): Promise<GateNotice[]> {
  const params = new URLSearchParams({ category });
  if (coralType) params.set('coral_type', coralType);
  const res = await apiClient.get<{ data: GateNotice[] }>(`${base(aquariumId)}/livestock-check?${params.toString()}`);
  return Array.isArray(res?.data) ? res.data : [];
}
